import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InstallerLogo, appConfig } from "@/branding/Branding";
import { EngineerAvatar } from "@/components/installer/EngineerAvatar";
import { Calendar, Clock, User as UserIcon, Info } from "lucide-react";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";

const STATUS_STYLE = {
    scheduled: { cls: "status-not-started", label: "Scheduled" },
    completed: { cls: "status-complete", label: "Completed" },
    cancelled: { cls: "status-in-progress", label: "Cancelled" },
};

function formatDate(iso) {
    try { return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }
    catch { return ""; }
}

/**
 * Appointments timeline. On top of the mobile-app parity, adds a "Your team"
 * legend and a left-edge accent bar on each card in the assigned engineer's
 * calendar colour, so customers can instantly map a visit to a face.
 */
export default function Appointments() {
    const { user } = useAuth();
    const [projectId, setProjectId] = useState(null);
    const [appts, setAppts] = useState([]);
    const [engineers, setEngineers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const p = user?.role === "installer"
                    ? (await api.listProjects())[0]
                    : await api.myProject();
                if (!p) return;
                setProjectId(p.id);
                const [a, engs] = await Promise.all([
                    api.listAppointments(p.id),
                    api.listEngineers({ includeArchived: true }).catch(() => []),
                ]);
                setAppts(a);
                setEngineers(engs);
            } finally { setLoading(false); }
        })();
    }, [user]);

    const engineerById = useMemo(() => {
        const m = new Map();
        (engineers || []).forEach((e) => m.set(e.id, e));
        return m;
    }, [engineers]);

    // The legend covers every distinct engineer referenced by the customer's
    // visits — matched by engineer_id when present, otherwise by displayName.
    const legend = useMemo(() => {
        const seen = new Map();
        for (const a of appts) {
            let e = null;
            if (a.engineer_id && engineerById.has(a.engineer_id)) e = engineerById.get(a.engineer_id);
            else e = (engineers || []).find((x) => x.displayName === a.engineer_name);
            if (!e) continue;
            if (!seen.has(e.id)) seen.set(e.id, e);
        }
        return Array.from(seen.values());
    }, [appts, engineers, engineerById]);

    const engineerForAppt = (a) => {
        if (a.engineer_id && engineerById.has(a.engineer_id)) return engineerById.get(a.engineer_id);
        return (engineers || []).find((x) => x.displayName === a.engineer_name) || null;
    };

    const now = new Date();
    const upcoming = appts.filter((a) => new Date(a.scheduled_at) >= now || a.status === "scheduled");
    const past = appts.filter((a) => !(new Date(a.scheduled_at) >= now || a.status === "scheduled"));

    const backTo = user?.role === "installer" ? "/installer" : "/dashboard";

    return (
        <div className="min-h-screen bg-[#F5F5F7] pb-20" data-testid="appointments-page">
            <PageContainer width="medium">
                <PageHeader
                    eyebrow={user?.role === "installer" ? "FIELD USER" : "CUSTOMER"}
                    title="Appointments"
                    subtitle="All visits from your Pearl Energy team, past and upcoming."
                    icon={Calendar}
                    backFallback={backTo}
                    testid="appointments"
                />

                {legend.length > 0 && (
                    <div className="mt-4 pe-card p-4 md:p-5" data-testid="appointments-legend">
                        <div className="text-[11px] text-[#4B5565] tracking-[0.14em] font-medium uppercase mb-2">Your team</div>
                        <div className="flex flex-wrap gap-3">
                            {legend.map((e) => (
                                <div key={e.id} className="flex items-center gap-2 pr-3" data-testid={`legend-${e.id}`}>
                                    <EngineerAvatar eng={e} size={28} />
                                    <div className="text-sm text-[#131B2F] font-medium">{e.displayName}</div>
                                    <div className="text-xs text-[#4B5565]">· {e.role}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="mt-6 pe-card p-8 text-center text-sm text-[#4B5565]">Loading appointments…</div>
                ) : appts.length === 0 ? (
                    <div className="mt-6 pe-card p-8 text-center text-sm text-[#4B5565]">No appointments yet.</div>
                ) : (
                    <>
                        <Section title="Upcoming" items={upcoming} empty="No upcoming visits scheduled." engineerForAppt={engineerForAppt} />
                        <Section title="Past" items={past} empty="No past visits yet." engineerForAppt={engineerForAppt} />
                    </>
                )}
            </PageContainer>
        </div>
    );
}

function Section({ title, items, empty, engineerForAppt }) {
    return (
        <div className="mt-8">
            <div className="text-base text-[#131B2F] font-medium mb-3">{title}</div>
            {items.length === 0 ? (
                <div className="pe-card p-5 text-sm text-[#4B5565]">{empty}</div>
            ) : (
                <ol className="space-y-3">
                    {items.map((a) => {
                        const s = STATUS_STYLE[a.status] || STATUS_STYLE.scheduled;
                        const eng = engineerForAppt(a);
                        const accent = eng?.colour || "#3E9E2E";
                        return (
                            <li
                                key={a.id}
                                data-testid={`appt-${a.id}`}
                                className="pe-card pe-card-hover p-5 relative overflow-hidden"
                                style={{ borderLeft: `4px solid ${accent}` }}
                            >
                                <div className="flex items-start gap-4">
                                    {eng ? (
                                        <EngineerAvatar eng={eng} size={44} />
                                    ) : (
                                        <span className="w-11 h-11 rounded-full bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center shrink-0">
                                            <Calendar size={18} strokeWidth={1.75} />
                                        </span>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                            <div className="text-base md:text-lg text-[#131B2F] font-medium">{a.title}</div>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${s.cls}`}>{s.label}</span>
                                        </div>
                                        <div className="mt-1 text-sm text-[#4B5565]">{formatDate(a.scheduled_at)}</div>
                                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#131B2F]">
                                            <span className="inline-flex items-center gap-1.5"><Clock size={13} strokeWidth={1.75} className="text-[#4B5565]" /> {a.arrival_window}</span>
                                            <span className="inline-flex items-center gap-1.5"><UserIcon size={13} strokeWidth={1.75} className="text-[#4B5565]" /> {a.engineer_name}</span>
                                            <span className="pe-chip">{a.appointment_type}</span>
                                        </div>
                                        {a.customer_instructions && (
                                            <div className="mt-3 flex items-start gap-2 text-sm text-[#4B5565] bg-[#F5F5F7] border border-[#E5E7EB] rounded-md p-3">
                                                <Info size={13} strokeWidth={1.75} className="mt-0.5 text-[#3E9E2E] shrink-0" />
                                                <span>{a.customer_instructions}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}
